import { useCallback, useEffect, useState } from 'react';
import { settingsApi } from '@/features/ai-dart/api/settings';
import { DartFormat } from '@/features/ai-dart/format';
import type { Schedule } from '@/features/ai-dart/types';
import { ActionButton, ActiveDot, TABLE, TD, TH, THEAD, TR } from '@/features/ai-dart/components/controls';
import { ConfirmModal } from '@/shared/ui/Modal';

const PRESETS = [
  { label: '5분마다', value: '*/5 * * * *' },
  { label: '10분마다', value: '*/10 * * * *' },
  { label: '15분마다', value: '*/15 * * * *' },
  { label: '20분마다', value: '*/20 * * * *' },
  { label: '30분마다', value: '*/30 * * * *' },
  { label: '1시간마다', value: '0 * * * *' },
  { label: '2시간마다', value: '0 */2 * * *' },
  { label: '6시간마다', value: '0 */6 * * *' },
];

const SCHEDULE_LABELS: Record<string, { title: string; description: string }> = {
  'ai-dart-triage-sched': { title: '티켓 수집 · triage', description: '소스 수집 → 티켓 발급' },
  'ai-dart-dispatch-sched': { title: '자동수정 디스패치 · dispatch', description: 'auto=allow 티켓 → autofix PR' },
  'ai-dart-poller-sched': { title: 'PR 폴러 · poller', description: 'PR 머지 감지 → 티켓 종결 + Sentry resolve' },
  'ai-dart-record-sched': { title: '하네스 기록 · record', description: '머지된 티켓 → 그룹 하네스에 사후 기록' },
  'ai-dart-notifier-sched': { title: '인수 요청 통지 · notifier', description: 'needs_human 티켓 → 슬랙 채널 알림' },
};

export function SchedulesView(): React.JSX.Element {
  const [rows, setRows] = useState<Schedule[]>([]); const [message, setMessage] = useState('');
  const load = useCallback(() => settingsApi.schedules.list().then(setRows).catch((error: Error) => setMessage(error.message)), []);
  useEffect(() => { void load(); }, [load]);
  const [busy, setBusy] = useState('');
  /** 조작 중에는 같은 화면의 다른 조작도 막는다 — 연타 한 번이 곧 잡 중복 실행이다. */
  const run = async (key: string, work: () => Promise<unknown>): Promise<void> => {
    if (busy) return;
    setBusy(key);
    try { await work(); setMessage('반영했습니다.'); load(); }
    catch (error) { setMessage((error as Error).message); }
    finally { setBusy(''); }
  };
  /**
   * 즉시 실행은 콘솔에서 가장 위험한 조작이다 — dispatch 잡은 여러 저장소에 PR을 만들고, 비가역이며
   * 과금된다. 같은 화면군의 삭제·폐기·비활성화가 모두 확인을 받는데 이것만 없었다.
   */
  const [runningNow, setRunningNow] = useState<Schedule | null>(null);
  const runNowMeta = runningNow ? SCHEDULE_LABELS[runningNow.id] : undefined;
  return (
    <section className="rounded-lg border border-line bg-surface">
      <header className="border-b border-line p-4">
        <h2 className="text-[13px] font-bold">스케줄</h2>
        <p className="whitespace-pre-line text-[11px] text-faint">AI-DART 파이프라인 잡의 주기와 사용 여부를 관리합니다. 저장 즉시 Cloud Scheduler에 반영됩니다.{'\n'}▶ 지금 실행은 중지 상태에서도 가능한 독립적인 1회 실행입니다.{'\n'}시각 기준은 각 잡의 타임존이며, 콘솔의 일반 시각 표시는 KST입니다.{'\n'}'마지막 실행'은 Cloud Scheduler가 그 잡을 호출한 시각(KST)입니다 — 잡이 무엇을 했는지는 각 잡의 로그에서 봅니다.</p>
      </header>
      {message && <p className="border-b border-line bg-line-soft p-2 px-4 text-[12px]">{message}</p>}
      <div className="overflow-x-auto">
        <table className={TABLE}>
          <thead className={THEAD}>
            <tr>
              <th className={TH}>사용</th>
              <th className={TH}>작업</th>
              <th className={TH}>주기</th>
              <th className={TH}>현재 cron</th>
              <th className={TH}>잡 타임존</th>
              <th className={TH}>마지막 실행</th>
              <th className={`${TH} text-right`}>수동 실행</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const meta = SCHEDULE_LABELS[row.id] ?? { title: row.id, description: '' };
              const on = row.state === 'ENABLED';
              return (
                <tr key={row.id} className={TR}>
                  <td className={TD}>
                    <div className="flex items-center gap-2">
                      <ActiveDot on={on} />
                      <ActionButton
                        tone={on ? 'default' : 'primary'}
                        disabled={busy !== ''}
                        onClick={() => void run(`toggle:${row.id}`, () => settingsApi.schedules.update(row.id, { enabled: !on }))}
                      >
                        {on ? '중지' : '사용'}
                      </ActionButton>
                    </div>
                  </td>
                  <td className={TD}>
                    <div className="font-medium">{meta.title}</div>
                    {meta.description && <div className="text-[10px] text-faint">{meta.description}</div>}
                  </td>
                  <td className={TD}>
                    <select
                      value={row.schedule}
                      disabled={busy !== ''}
                      onChange={(event) => void run(`cron:${row.id}`, () => settingsApi.schedules.update(row.id, { schedule: event.target.value }))}
                      className="cursor-pointer rounded-md border border-line bg-surface px-2 py-1 text-[11px] transition hover:border-accent-line disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {!PRESETS.some((item) => item.value === row.schedule) && <option value={row.schedule}>사용자 지정: {row.schedule}</option>}
                      {PRESETS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                    </select>
                  </td>
                  <td className={`${TD} font-mono`}>{row.schedule}</td>
                  <td className={`${TD} text-muted`}>{row.timeZone || '-'}</td>
                  <td className={`${TD} whitespace-nowrap text-faint`}>{DartFormat.kst(row.lastAttemptTime)}</td>
                  <td className={TD}>
                    <div className="flex justify-end">
                      <ActionButton
                        tone="danger"
                        disabled={busy !== ''}
                        title="주기와 무관한 1회 실행 — 되돌릴 수 없습니다"
                        onClick={() => setRunningNow(row)}
                      >
                        {busy === `run:${row.id}` ? '실행 중…' : '▶ 지금 실행'}
                      </ActionButton>
                    </div>
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && <tr><td className="p-8 text-center text-muted" colSpan={7}>표시할 스케줄이 없습니다.</td></tr>}
          </tbody>
        </table>
      </div>

      <ConfirmModal
        open={runningNow !== null}
        title="지금 1회 실행"
        confirmLabel="지금 실행"
        busy={busy !== ''}
        onClose={() => setRunningNow(null)}
        onConfirm={() => { const row = runningNow; setRunningNow(null); if (row) void run(`run:${row.id}`, () => settingsApi.schedules.run(row.id)); }}
      >
        <strong>{runNowMeta?.title ?? runningNow?.id}</strong>을(를) 주기와 무관하게 즉시 1회 실행합니다.
        {runNowMeta?.description && <> — {runNowMeta.description}</>}
        <br />중지 상태에서도 실행되며 <strong>시작한 뒤에는 되돌릴 수 없습니다</strong>.
      </ConfirmModal>
    </section>
  );
}
